import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import noop from 'lodash/noop';

import { useSnackBar } from '@sbv-clients/ui-shared';
import { getApiErrorMessage } from '@sbv-clients/util-misc';

import {
  Api,
  CreateOpenShiftRequest,
  FacilityCacheKeys,
  ManageScheduleCacheKeys,
  OpenShiftConfiguration,
  OpenShiftsCacheKeys,
  useApi,
} from '../index';

const createOpenShift =
  (api: Api) => (createOpenShiftRequest: CreateOpenShiftRequest) =>
    api.openShiftApi.openShiftPost({ createOpenShiftRequest });

export const useCreateOpenShift = () => {
  const api = useApi();
  const queryClient = useQueryClient();

  const { notifySuccess, notifyError } = useSnackBar();
  return useMutation(createOpenShift(api), {
    onSuccess: () => {
      notifySuccess('Created successfully');
      queryClient
        .invalidateQueries([ManageScheduleCacheKeys.DisplaySchedule])
        .catch(noop);
      queryClient
        .invalidateQueries([ManageScheduleCacheKeys.DisplayViewSummary])
        .catch(noop);
      queryClient
        .invalidateQueries([ManageScheduleCacheKeys.AssignedEmployees])
        .catch(noop);
      queryClient
        .invalidateQueries([ManageScheduleCacheKeys.NotifiedEmployees])
        .catch(noop);
      queryClient
        .invalidateQueries([FacilityCacheKeys.OpenShiftsV2])
        .catch(noop);
      queryClient
        .invalidateQueries([FacilityCacheKeys.OpenShiftsEmployeesV2])
        .catch(noop);
    },
    onError: async (error) => {
      notifyError(getApiErrorMessage(error));
    },
  });
};
export const useOpenShiftsConfigGet = () => {
  const api = useApi();
  return useQuery({
    queryKey: [OpenShiftsCacheKeys.Config],
    queryFn: () =>
      api.openShiftApi
        .openShiftConfigGet()
        .then((response) => response.data ?? {}),
    retry: false,
    refetchOnWindowFocus: false,
  });
};

const openShiftsConfigPut =
  (api: Api) => (openShiftConfiguration: OpenShiftConfiguration) =>
    api.openShiftApi.openShiftConfigPut({ openShiftConfiguration });

export const useOpenShiftsConfigPut = () => {
  const queryClient = useQueryClient();
  const api = useApi();
  return useMutation(openShiftsConfigPut(api), {
    onSuccess: async () => {
      await queryClient
        .invalidateQueries([OpenShiftsCacheKeys.Config])
        .catch(noop);
    },
  });
};
